import React, { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";

const Events = () => {
  const { t } = useTranslation();
  const [isVisible, setIsVisible] = useState(false);
  const [activeEvent, setActiveEvent] = useState(0);
  const sectionRef = useRef(null);

  const events = [
    {
      id: "national",
      title: t("events.types.national.title"),
      description: t("events.types.national.description"),
      action: t("events.types.national.action"),
      type: "National",
      color: "from-emerald-500 to-teal-500",
      bgColor: "from-emerald-50 to-teal-50",
      icon: (
        <svg
          className="w-10 h-10"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="2"
            d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
          />
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="2"
            d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
          />
        </svg>
      ),
      features: ["Local Impact", "Community Focus", "Grassroots Change"],
    },
    {
      id: "international",
      title: t("events.types.international.title"),
      description: t("events.types.international.description"),
      action: t("events.types.international.action"),
      type: "International",
      color: "from-teal-500 to-cyan-500",
      bgColor: "from-teal-50 to-cyan-50",
      icon: (
        <svg
          className="w-10 h-10"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="2"
            d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
          />
        </svg>
      ),
      features: ["Global Reach", "Cross-Cultural", "European Network"],
    },
    {
      id: "conference",
      title: t("events.types.conference.title"),
      description: t("events.types.conference.description"),
      action: t("events.types.conference.action"),
      type: "Conference",
      color: "from-emerald-600 to-teal-600",
      bgColor: "from-emerald-50 to-teal-50",
      icon: (
        <svg
          className="w-10 h-10"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="2"
            d="M15 10l4.553-2.276A2 2 0 0020 6.382V5a2 2 0 00-2-2H6a2 2 0 00-2 2v1.382a2 2 0 00.447 1.342L9 10m6 0v4a2 2 0 01-2 2H7a2 2 0 01-2-2v-4m6 0l-6-3"
          />
        </svg>
      ),
      features: ["Expert Speakers", "Networking", "Knowledge Sharing"],
    },
  ];

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  // Auto-slider effect
  useEffect(() => {
    const interval = setInterval(() => {
      setActiveEvent((prev) => (prev + 1) % events.length);
    }, 4000);
    return () => clearInterval(interval);
  }, [events.length]);

  return (
    <section
      ref={sectionRef}
      id="events"
      className="py-24 relative overflow-hidden"
      style={{ backgroundColor: "var(--background-color)" }}
    >
      {/* Creative Background */}
      <div className="absolute inset-0">
        <div className="absolute top-0 left-0 w-full h-full bg-gradient-to-br from-emerald-50/40 via-transparent to-teal-50/40"></div>
        <div className="absolute top-10 right-10 w-64 h-64 bg-gradient-to-r from-emerald-200/30 to-teal-200/30 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute bottom-10 left-10 w-80 h-80 bg-gradient-to-r from-teal-200/30 to-cyan-200/30 rounded-full blur-3xl animate-pulse delay-1000"></div>
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-gradient-to-r from-emerald-100/20 to-teal-100/20 rounded-full blur-3xl animate-pulse delay-500"></div>
      </div>

      <div className="relative max-w-7xl mx-auto px-4 md:px-8">
        {/* Section Header */}
        <div
          className={`text-center mb-20 transition-all duration-1000 ${
            isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"
          }`}
        >
          <h2 className="text-5xl md:text-6xl font-bold mb-6">
            <span className="bg-gradient-to-r from-emerald-600 via-teal-600 to-cyan-600 bg-clip-text text-transparent">
              {t("events.title")}
            </span>
          </h2>
          <p className="text-xl text-gray-600 max-w-4xl mx-auto leading-relaxed">
            Discover our diverse range of events designed to challenge
            assumptions, promote inclusion, and create meaningful change across
            Europe
          </p>
        </div>

        {/* Interactive Event Showcase */}
        <div
          className={`mb-16 transition-all duration-1000 delay-300 ${
            isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"
          }`}
        >
          {/* Event Navigation */}
          <div className="flex flex-wrap justify-center gap-4 mb-12">
            {events.map((event, index) => (
              <button
                key={event.id}
                onClick={() => setActiveEvent(index)}
                className={`group relative px-8 py-4 rounded-2xl font-semibold transition-all duration-300 ${
                  activeEvent === index
                    ? `bg-gradient-to-r ${event.color} text-white shadow-lg scale-105`
                    : "bg-white text-gray-600 hover:text-gray-900 hover:shadow-md"
                }`}
              >
                <div className="flex items-center space-x-3">
                  <div
                    className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                      activeEvent === index
                        ? "bg-white/20"
                        : `bg-gradient-to-r ${event.color} text-white`
                    }`}
                  >
                    {event.icon}
                  </div>
                  <span>{event.type}</span>
                </div>
                {activeEvent === index && (
                  <div className="absolute -bottom-2 left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-white"></div>
                )}
              </button>
            ))}
          </div>

          {/* Featured Event Display */}
          <div className="relative">
            <div className="bg-white rounded-3xl shadow-2xl overflow-hidden transition-all duration-500">
              <div
                className={`h-2 bg-gradient-to-r ${events[activeEvent].color} transition-all duration-500`}
              ></div>
              <div className="p-12">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
                  {/* Event Info */}
                  <div className="space-y-8">
                    <div className="space-y-4">
                      <div
                        className={`inline-flex items-center px-4 py-2 rounded-full text-sm font-semibold bg-gradient-to-r ${events[activeEvent].color} text-white transition-all duration-500`}
                      >
                        {events[activeEvent].type} Event
                      </div>
                      <h3 className="text-4xl font-bold text-gray-900 transition-all duration-500">
                        {events[activeEvent].title}
                      </h3>
                      <p className="text-xl text-gray-600 leading-relaxed transition-all duration-500">
                        {events[activeEvent].description}
                      </p>
                    </div>

                    {/* Event Features */}
                    <div className="space-y-4">
                      <h4 className="text-lg font-semibold text-gray-900">
                        Key Features:
                      </h4>
                      <div className="flex flex-wrap gap-3">
                        {events[activeEvent].features.map((feature, index) => (
                          <span
                            key={index}
                            className={`px-4 py-2 rounded-full text-sm font-medium bg-gradient-to-r ${events[activeEvent].bgColor} text-gray-700 transition-all duration-500`}
                          >
                            {feature}
                          </span>
                        ))}
                      </div>
                    </div>

                    {/* Action Button - Hidden */}
                    {/* <div className="pt-4">
                      <button className={`group relative px-8 py-4 rounded-2xl font-semibold text-white overflow-hidden transition-all duration-500 hover:scale-105 bg-gradient-to-r ${events[activeEvent].color}`}>
                        <span className="relative z-10 flex items-center">
                          {events[activeEvent].action}
                          <svg className="w-5 h-5 ml-2 transition-transform duration-300 group-hover:translate-x-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
                          </svg>
                        </span>
                        <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-700"></div>
                      </button>
                    </div> */}
                  </div>

                  {/* Event Visual */}
                  <div className="relative">
                    <div
                      className={`w-32 h-32 mx-auto mb-8 bg-gradient-to-r ${events[activeEvent].color} rounded-3xl flex items-center justify-center text-white shadow-2xl transition-all duration-500`}
                    >
                      {events[activeEvent].icon}
                    </div>
                    <div className="text-center">
                      <div className="w-24 h-24 mx-auto bg-gradient-to-r from-gray-100 to-gray-200 rounded-full flex items-center justify-center mb-4">
                        <svg
                          className="w-12 h-12 text-gray-400"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth="2"
                            d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                          />
                        </svg>
                      </div>
                      <p className="text-gray-500 font-medium">Coming Soon</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Events;
